import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './maintenanceQueue.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider } from '../../../components/AppToolbar';
import { useConfirm } from '../../../components/confirm';
import { serverMessage } from '../../../components/confirm/serverMessage';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { runLoading } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { ViewToggle } from '../../../components/ViewToggle';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect } from '../../modules/DateRangeSelect';
import {
  fetchLog, fetchLogSetups, rollbackRecord, type LogEntry, type LogSetupRow,
} from './api';
import MaintenanceDonePage from './MaintenanceDonePage';

// ─────────────────────────────────────────────────────────────────────────────
// Instrument Management ▸ Maintenance Log (nav `instm-maint-log`). Port of legacy ALPACA's three-step Maintenance Log:
//   1. `MaintenanceSetup_ListView_Log` — the setups that have been maintained (the shared module list);
//   2. click one → "Maintained Log - {instrument}" (`MaintenanceTaskCheckList_ListView_MaintainedLog`): its finished
//      occurrences with the Date Filter (1M … All) and the Rollback action;
//   3. click a row → the read-only Maintained Record (MaintenanceDonePage).
// Each step REPLACES the one before (standing rule 12); the List View / Detail View switch opens the ticked row, or the first.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Maintenance Queue';

type Screen =
  | { kind: 'setups' }
  | { kind: 'log'; setup: LogSetupRow }
  | { kind: 'done'; setup: LogSetupRow; oid: string };

/** G5 Date Filter choices (1M · 3M · 6M · 1Y · 2Y · 5Y · All) — `days` is only the chip's identity; the months go to the API. */
const LOG_RANGES = [
  { label: '1M', days: 30 }, { label: '3M', days: 90 }, { label: '6M', days: 180 },
  { label: '1Y', days: 365 }, { label: '2Y', days: 730 }, { label: '5Y', days: 1825 }, { label: 'All', days: null },
];
const MONTHS: Record<string, number | null> = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '2Y': 24, '5Y': 60, All: null };

const SETUP_COLUMNS: AppColumn<LogSetupRow>[] = [
  { field: 'instrumentId', header: 'InstrumentID', width: 110 },
  { field: 'instrumentName', header: 'InstrumentName', width: 220 },
  { field: 'department', header: 'Department', width: 160 },
  { field: 'category', header: 'Category', width: 260 },
  { field: 'lastMaintenanceDate', header: 'LastMaintenanceDate', width: 150, type: 'date' },
  { field: 'nextMaintenanceDate', header: 'NextMaintenanceDate', width: 150, type: 'date' },
];

const ENTRY_COLUMNS: AppColumn<LogEntry>[] = [
  { field: 'maintenanceId', header: 'MaintenanceId', width: 140 },
  { field: 'instrumentId', header: 'Instrument ID', width: 120 },
  { field: 'instrumentName', header: 'Instrument Name', width: 200 },
  { field: 'frequency', header: 'Frequency', width: 110 },
  { field: 'tasks', header: 'Tasks', width: 240 },
  { field: 'ownership', header: 'Ownership', width: 160 },
  { field: 'maintainedDate', header: 'MaintainedDate', width: 130, type: 'date' },
  { field: 'maintainedBy', header: 'MaintainedBy', width: 150 },
  { field: 'status', header: 'Status', width: 110 },
];

export default function MaintenanceLogPage({ onHome, pageId = 'instm-maint-log' }: { onHome?: () => void; pageId?: string }) {
  const notify = useNotify();
  const { prompt } = useConfirm();
  const perm = useNavActions(pageId);
  const [screen, setScreen] = useState<Screen>({ kind: 'setups' });

  // Step 1 — the setups
  const [setups, setSetups] = useState<LogSetupRow[]>([]);
  const [setupSel, setSetupSel] = useState<Set<string | number>>(new Set());
  // Step 2 — a setup's maintained log
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [entrySel, setEntrySel] = useState<Set<string | number>>(new Set());
  const [range, setRange] = useState('1M');   // G5 opens on 1M (MaintenanceQueueDateFilter item 0)

  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const loadSetups = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setSetups(await fetchLogSetups());
      setSetupSel(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the maintenance log.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  const setupOid = screen.kind === 'setups' ? null : screen.setup.oid;
  const loadEntries = useCallback(async (oid: string, code: string) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setEntries(await fetchLog(oid, MONTHS[code] ?? null));
      setEntrySel(new Set());
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Failed to load the maintained log.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { if (screen.kind === 'setups') void loadSetups(); }, [screen.kind, loadSetups]);
  useEffect(() => { if (setupOid && screen.kind === 'log') void loadEntries(setupOid, range); }, [setupOid, screen.kind, range, loadEntries]);

  const text = (v: unknown) => String(v ?? '').toLowerCase();
  const shownSetups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? setups : setups.filter((r) => [r.instrumentId, r.instrumentName, r.department, r.category].some((v) => text(v).includes(q)));
  }, [setups, search]);
  const shownEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? entries : entries.filter((r) => [r.maintenanceId, r.instrumentName, r.frequency, r.tasks, r.ownership, r.maintainedBy, r.status]
      .some((v) => text(v).includes(q)));
  }, [entries, search]);

  const openSetup = (setup: LogSetupRow) => { setSearch(''); setScreen({ kind: 'log', setup }); };
  const oneSetup = setupSel.size === 1 ? shownSetups.find((r) => r.oid === String([...setupSel][0])) : undefined;
  const oneEntry = entrySel.size === 1 ? String([...entrySel][0]) : null;

  async function rollbackSelected() {
    if (!oneEntry) { notify.warning('Please select an item to proceed further.'); return; }
    const reason = await prompt({ title: 'Rollback Reason', message: 'Please enter the rollback reason.', required: true });
    if (reason === null) return;
    try {
      await runLoading(rollbackRecord(oneEntry, reason), { blocking: true, label: 'Rolling back…' });
      notify.success('Rollback completed successfully for all related tasks.');
      if (setupOid) void loadEntries(setupOid, range);
    } catch (e) {
      notify.warning(serverMessage(e) ?? 'Rollback failed.');
    }
  }

  // Step 3 — the read-only Maintained Record
  if (screen.kind === 'done') {
    const setup = screen.setup;
    return (
      <MaintenanceDonePage
        key={screen.oid}
        oid={screen.oid}
        siblingIds={shownEntries.map((r) => r.oid)}
        canWrite={perm.write}
        onHome={onHome ?? (() => undefined)}
        onClose={() => { setScreen({ kind: 'log', setup }); void loadEntries(setup.oid, range); }}
        onNavigate={(oid) => setScreen({ kind: 'done', setup, oid })}
      />
    );
  }

  const inLog = screen.kind === 'log';
  const title = inLog ? `Maintained Log - ${screen.setup.instrumentName}` : undefined;

  const setupCols: AppColumn<LogSetupRow>[] = [
    { field: '__open', header: '', width: 40, sortable: false, filterable: false, noExport: true,
      render: (r) => (
        <button type="button" className="mlv-rowact" title="Open" aria-label={`Open ${r.instrumentName}`}
                onClick={(e) => { e.stopPropagation(); openSetup(r); }}>
          <i className="ti ti-edit" aria-hidden="true" />
        </button>
      ) },
    ...SETUP_COLUMNS,
  ];
  const entryCols: AppColumn<LogEntry>[] = [
    { field: '__open', header: '', width: 40, sortable: false, filterable: false, noExport: true,
      render: (r) => (
        <button type="button" className="mlv-rowact" title="Open" aria-label={`Open ${r.maintenanceId ?? 'record'}`}
                onClick={(e) => { e.stopPropagation(); setScreen({ kind: 'done', setup: (screen as { setup: LogSetupRow }).setup, oid: r.oid }); }}>
          <i className="ti ti-edit" aria-hidden="true" />
        </button>
      ) },
    ...ENTRY_COLUMNS,
  ];

  const detailTarget = () => {
    if (inLog) {
      const id = oneEntry ?? shownEntries[0]?.oid;
      if (id) setScreen({ kind: 'done', setup: screen.setup, oid: id });
    } else {
      const s = oneSetup ?? shownSetups[0];
      if (s) openSetup(s);
    }
  };

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Instrument Management</span>
          <i className="ti ti-chevron-right" />
          {inLog
            ? <a onClick={() => { setSearch(''); setScreen({ kind: 'setups' }); }} style={{ cursor: 'pointer' }}>Maintenance Log</a>
            : <span className="dp-bc-active">Maintenance Log</span>}
          {inLog && (<><i className="ti ti-chevron-right" /><span className="dp-bc-active">{title}</span></>)}
        </div>
        <div style={{ padding: '2px 0 8px' }}>
          <ViewToggle view="list" detailDisabled={inLog ? shownEntries.length === 0 : shownSetups.length === 0}
                      onChange={(v) => { if (v === 'detail') detailTarget(); }} />
        </div>
        <FormTitle pageId={pageId} title={title} loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />
        {inLog ? (
          <AppToolbar
            onRefresh={() => loadEntries(screen.setup.oid, range)}
            refreshing={loading}
            beforeRefresh={
              <>
                <ToolbarDivider />
                <DateRangeSelect variant="dropdown" label="Date Filter" ranges={LOG_RANGES}
                  value={LOG_RANGES.find((r) => r.label === range)?.days ?? null}
                  onChange={(days) => setRange(LOG_RANGES.find((r) => r.days === days)?.label ?? 'All')} />
              </>
            }
            beforeExport={
              <button type="button" className="ca-btn" disabled={!oneEntry || !perm.write} onClick={() => void rollbackSelected()} title="Rollback">
                <span className="tb-i tb-i-red">↩︎</span> Rollback
              </button>
            }
            exportData={{ filename: 'maintained-log', ...gridExportMatrix(shownEntries, ENTRY_COLUMNS) }}
          />
        ) : (
          <AppToolbar
            onRefresh={() => loadSetups()}
            refreshing={loading}
            exportData={{ filename: 'maintenance-log', ...gridExportMatrix(shownSetups, SETUP_COLUMNS) }}
          />
        )}
        <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
        {inLog ? (
          <AppDataGrid<LogEntry>
            rows={shownEntries} columns={entryCols} getRowId={(r) => r.oid} loading={loading}
            selectable selectedIds={entrySel} onSelectionChange={setEntrySel}
            onRowDoubleClick={(r) => setScreen({ kind: 'done', setup: screen.setup, oid: r.oid })}
            defaultPageSize={20} emptyText={search ? 'No results match your search.' : 'No data to display'}
          />
        ) : (
          <AppDataGrid<LogSetupRow>
            rows={shownSetups} columns={setupCols} getRowId={(r) => r.oid} loading={loading}
            selectable selectedIds={setupSel} onSelectionChange={setSetupSel}
            onRowDoubleClick={openSetup}
            defaultPageSize={20} emptyText={search ? 'No results match your search.' : 'No data to display'}
          />
        )}
      </div>
    </div>
  );
}
